import BlockContent from '@sanity/block-content-to-react'
import imageUrlBuilder from '@sanity/image-url'
import { Button, Col, Form, Input, Row } from 'antd'
import axios from 'axios'
import { darken } from 'polished'
import React, { Component, Fragment } from 'react'
import { Head, RouteData, withSiteData } from 'react-static'
import styled, { withTheme } from 'styled-components'
import Breakpoint from '../components/Breakpoint'
import Hero from '../components/Hero'
import HostsList from '../components/HostsList'
import Seperator from '../components/Seperator'
import { styledf } from '../styles/styled-common'
import { isEmail } from '../utils'
import getSerializers from '../utils/sanitySerializers'

const TopHero = styled(Hero)`
  color: ${p => p.theme.textOffwhite};
  padding-bottom: 6px;
`

const formPadding = 8
const MailingRow = styled(Row)`
  background-color: #00000075;
  margin: 16px 0;
  padding: 20px 20px ${20 - formPadding}px;
  font-size: 1.3em;
`

const MailingForm = styledf(['broke', 'showingHelp'])(Form)`
  display: inline-block;
  text-align: ${p => (p.broke ? 'center' : 'left')};
  margin: ${p => `${p.broke ? formPadding : 0}px 10px`}
  vertical-align: top;
  transition: margin .2s;

  .has-feedback {
    width: 100%;
  }

  .ant-form-item-with-help {
    margin-bottom: 0;
  }

  .ant-form-item:first-child {
    width: ${p => (p.broke ? 'auto' : '260px')};
    margin-right: 0;
    .ant-form-item-control-wrapper {
      width: 100%;
    }
  }

  .ant-form-item:last-child {
    ${p => p.broke && 'margin-left: 16px'}
    margin-right: 0;
    margin-left: 16px;
  }
`

const MailInput = styled(Input)`
  margin-bottom: ${formPadding}px;

  &::placeholder {
    font-size: 0.9em;
  }
`

const InlineDiv = styled.div`
  display: inline-block;
  vertical-align: middle;
  vertical-align: text-top;
`

const Block = styled(BlockContent)`
  text-align: left;
  h1 {
    text-align: center;
  }
  p {
    font-size: 1.1em;
  }
`

class Home extends Component {
  state = {}

  constructor(props) {
    super(props)

    this._emailRef = React.createRef()
    const { marks, types } = getSerializers(props.siteData)

    this._serializers = {
      marks,
      types: {
        image: types.image,
      },
    }
  }

  _onFilterChanged(filter) {
    // implement filter
  }

  _subscribe = async e => {
    e.preventDefault()
    const email = this._emailRef.current.input.value

    if (!isEmail(email)) {
      return this.setState({
        subscribeResult: {
          sucess: false,
          message: 'Enter a valid email address',
        },
      })
    }

    this.setState({ subscribing: true, subscribeResult: null })
    try {
      const response = await axios({
        method: 'post',
        url: '/.netlify/functions/subscribe',
        data: { email },
        headers: { 'Content-Type': 'application/json' },
      })

      const result = response.body
      const subscribeResult = {}

      if (response.status === 200) {
        subscribeResult.success = true
        subscribeResult.message = 'Success!'
      } else {
        subscribeResult.success = false
        subscribeResult.message =
          (response.status === 500 ? response.data.message : response.data) ||
          response.statusText
      }

      this.setState({
        subscribing: false,
        subscribeResult,
      })
    } catch ({ response }) {
      const subscribeResult = {
        success: false,
        message:
          (response.data && response.data.message) || response.statusText,
      }
      this.setState({
        subscribing: false,
        subscribeResult,
      })
    }
  }

  _handleSubmit() {
    if (err) {
      console.error(err)
      this.setState({ submitResult: err })
      return
    }

    console.log('Received values of form: ', values)
    this.setState({ submitResult: 'Subscribing...' })

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode(values),
    })
      .then(response => {
        if (response.stats !== 200) {
          throw new Error(`Error sending form. Status Code ${response.status}`)
        }

        this.setState({ submitResult: true })
      })
      .catch(error => {
        console.error(error)
        this.setState({ submitResult: error.message })
      })
  }

  render() {
    const { site, sanityConfig } = this.props.siteData
    const { subscribing, subscribeResult } = this.state

    const validStatus = subscribing
      ? 'validating'
      : subscribeResult && (subscribeResult.success ? 'success' : 'error')

    const validMessage = subscribeResult && subscribeResult.message

    return (
      <RouteData
        render={({ hosts, heroTextBlock, underListBlock }) => (
          <Fragment>
            <Head>
              <title>{site.title}</title>
              <meta
                name="description"
                content={`All the best ${
                  site.game.name
                } server hosting providers in one place. Use our comparison guide to choose perfect ${
                  site.game.shortName
                } hosting for you.`}
              />
              {/* SCHEMA  */}
              <script type="application/ld+json">
                {`{
                  "@context": "http://schema.org/",
                  "@type": "Organization",
                  "identifier": "${site.domain}",
                  "name": "${site.name}",
                  "logo": "${site.logo}",
                  "url": "${site.url}/"
                  }`}
              </script>
            </Head>
            <TopHero
              imagePlaceholder={heroTextBlock.imageAsset.metadata.lqip}
              imageSrc={imageUrlBuilder(sanityConfig)
                .image(heroTextBlock.image)
                .url()}
              color={darken(0.1, this.props.theme.main)}
              minHeight="36vh"
              isCentered
              parallaxOffset={200}
              opacity={0.2}
            >
              <Row type="flex" align="middle" justify="center">
                <Col xs={22} sm={20} md={18} xl={16}>
                  <Block blocks={heroTextBlock.body} />
                </Col>
              </Row>
              <MailingRow type="flex" align="middle" justify="center">
                <Col xs={22} sm={20} md={18} xl={16}>
                  <div>
                    Want us to let you know about game updates, new hosts,
                    special deals, coupons, and new features?
                  </div>
                  <div>
                    <InlineDiv>
                      Join the <strong>BestHosting Network</strong> mailing
                      list:
                    </InlineDiv>
                    {/* <InlineDiv> */}
                    <Breakpoint
                      width={992}
                      render={broke => (
                        <MailingForm
                          broke={broke}
                          showingHelp={!!this.state.subscribeResult}
                          layout="inline"
                          onSubmit={this._subscribe}
                        >
                          <Form.Item
                            validateStatus={validStatus}
                            help={validMessage}
                            hasFeedback
                          >
                            <MailInput
                              type="email"
                              ref={this._emailRef}
                              // onPressEnter={this._subscribe}
                              size={broke ? 'large' : 'default'}
                              placeholder="Your email address"
                            />
                          </Form.Item>
                          <Form.Item>
                            <Button
                              // onClick={this._subscribe}
                              htmlType="submit"
                              type="primary"
                              disabled={!!subscribing}
                              size={broke ? 'large' : 'default'}
                            >
                              Sign me up
                            </Button>
                          </Form.Item>
                        </MailingForm>
                      )}
                    />
                    {/* </InlineDiv> */}
                  </div>
                </Col>
              </MailingRow>
            </TopHero>
            <Seperator />
            <Row
              style={{ margin: '40px 0 20px' }}
              type="flex"
              align="middle"
              justify="center"
            >
              <Col xs={22} sm={20} md={18} xl={16}>
                <HostsList hosts={hosts} />
              </Col>
            </Row>
            <Row
              type="flex"
              align="middle"
              justify="center"
              style={{ margin: '40px 0 20px' }}
            >
              <Col xs={22} md={20} lg={14} xl={10}>
                {underListBlock && (
                  <React.Fragment>
                    <BlockContent
                      serializers={this._serializers}
                      projectId={sanityConfig.projectId}
                      dataset={sanityConfig.dataset}
                      imageOptions={{
                        thumbnail: { width: 320, fit: 'max' },
                        full: { width: 1024, height: 768, fit: 'max' },
                      }}
                      blocks={underListBlock.body}
                      isInline
                    />
                  </React.Fragment>
                )}
              </Col>
            </Row>
            <Seperator />
          </Fragment>
        )}
      />
    )
  }
}

export default withSiteData(withTheme(Home))



// WEBPACK FOOTER //
// /opt/build/repo/src/containers/Home.jsx